/* @flow */
import React from 'react';
import Formsy from 'lib/Formsy';

/**
  * Component used to create a Form. Leverages Formsy
  * https://github.com/christianalfoni/formsy-react
  *
  * ### General Form Structure
  *
  * This form component should be used whenever you need to **submit information**
  * (this should be obvious)
  * but there have been instances in our codebase in which forms have been faked.
  *
  * These are the general form components
  * * FormInput
  * * FormDatePicker
  * * FormNote
  * * FormSingle
  * * FormSelect
  * * FormMoneyInput
  * * FormCellInput
  * * FormCheckBox
  *
  * They are the Field component wrapped around a core component. (*see FormDatePicker*).
  *
  * ### Constructing a Form
  *
  * This is an example of a form. Every form has a `onValid` and `onInvalid` property
  * that is GENERALLY used to set the state of the component to disabled: false, or disabled: true.
  *
  * Then, a button that will handle what happens when the form is submitted is
  * disabled based on that
  * property.
  *
  * For example
  * ```
  * <Form
  *  ref="form"
  *  className="CreateEventForm-form"
  *  onValid={() => this.setState({disabled: false})}
  *  onInvalid={() => this.setState({disabled: true})}
  *  onValidSubmit={this.onSubmit}>
  *  <Button
  *     size="small"
  *     disabled={this.state.disabled}>
  *    Submit
  *  </Button>
  * </Form>
  * ```
  *
  * The `onValidSubmit` property takes a function that generally looks like this.
  * It should deal with the error case, generally by showing a Toast to indicate success or failure.
  * ```
  * onSubmit(value) {
  *   this.setState({loading: true});
  *   const onDone = () => {
  *     this.setState({loading: false});
  *   }
  *
  *   return SomeActions.create(
  *      value.foo,
  *      value.bar
  *    ).then(() =>
  *      () => ToastActions.showSuccessToast('Success'),
  *      () => ToastActions.showDefaultErrorToast())
  *    ).then(onDone);
  *
  * ```
  *
  * ### Creating your own validations
  *
  * A lot of times it is necessary to create your own validations, instead of
  * using Formsy's out of the box validators.
  *
  * To do this go to `Formsy.js` and add your own validator like this.
  * ```
  * Formsy.customValidators.isRequired = function(values, value) {
  *   return value === null || value === undefined || value === '';
  * };
  * Formsy.addValidationRule('isRequired', Formsy.customValidators.isRequired);
  * ```
  *
  * You can then use this in your own form like this
  *
  * ```
  * <FormInput validations={{isRequired: true} validationErrors={{isRequired: 'Input required'}}
  * ```
  *
  *
  * ### Creating your own Form component
  *
  *
  * In most cases, it's enough to pass in your own component into `FormInput`.
  *
  * ```
  * <FormInput
  *    name="location"
  *    component={LocationInput}
  *  />
  * ```
  * However, you might need to roll out your own. In this case look  at
  * `FormDatePicker`, `FormSelect` or `FormCheckbox`, as these are all examples
  * of custom Form components that wrap existing components.
  */

class Form extends React.Component {
  // public
  getCurrentValues = () => {
    return this.refs.formsy.getCurrentValues();
  };

  render() {
    return (
      <Formsy.Form ref="formsy" {...this.props} method="post">
        {this.props.children}
      </Formsy.Form>
    );
  }
}

export default Form;



// WEBPACK FOOTER //
// ./src/components/Form.jsx